import { AppShell as MantineAppShell, Menu, UnstyledButton } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { SystemRole } from "@eqms/shared-types";
import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";

import {
  compareNavTypes,
  firstDashboardPath,
  isNavTypeShown,
  isProjectsMenuShown,
  isTraceMenuConfigured,
  NAV_PATH,
  PROJECTS_NAV_REF,
  resolveModuleLabel,
} from "@/lib/nav/sidebarModules";
import { isTypePathActive } from "@/lib/nav/navPath";
import { usePopupInteractions } from "@/lib/popup-interactions";
import { readKey, setKey } from "@/lib/safeAccess";
import { ToastHost } from "@/components/ToastHost";

import { PasswordExpiredGate } from "@/components/settings/PasswordExpiredGate";
import { GlobalSearch } from "@/components/widgets/GlobalSearch";
import { NotificationCenter } from "@/components/widgets/NotificationCenter";
import { avatarColorFor, initialsOf } from "@/lib/dynamic/avatar";
import { resolveLabel } from "@/lib/i18n/resolveLabel";
import { useLang } from "@/lib/i18n/useLang";
import { CompanyBrand } from "@/components/CompanyBrand";
import { OrgSwitcher } from "@/components/OrgSwitcher";
import { setActiveCompany } from "@/lib/api-client";
import { logout } from "@/lib/auth";
import { useIdleLogout } from "@/lib/useIdleLogout";
import { MENU_DASHBOARD_OVERALL, MENU_DASHBOARD_PERSONAL } from "@/lib/dynamic/menuPermissions";
import { useMenuGate } from "@/lib/dynamic/useMenuGate";
import { roleLabel } from "@/lib/dynamic/roleCodes";
import { useTouchWorkspace } from "@/lib/queries/useLanding";
import { useMe } from "@/lib/queries/useMe";
import { useModules } from "@/lib/queries/useModules";
import { navSectionTitle, useNav } from "@/lib/queries/useNav";
import { useTypeCounts } from "@/lib/queries/useTypeCounts";
import { useWorkitemTypes, type WorkitemTypeResponse } from "@/lib/queries/useWorkitemTypes";
import { useWorkspaceDetail } from "@/lib/queries/useWorkspaces";
import { useUserStore } from "@/store/user-store";
import { useWorkspaceStore } from "@/store/workspace-store";

/**
 * eQMS 앱 셸 레이아웃 (동적 메타모델 · 하이브리드)
 *
 * 목업(Teamer eQMS standalone)의 셸 마크업/클래스를 픽셀 단위로 이식함.
 * Mantine AppShell 골격은 유지하되 헤더/네비 슬롯을 쓰지 않고,
 * 본문 한 칸(Main) 안에 목업 셸(.eqms-shell > .eqms-sidebar + .eqms-main)을 직접 렌더함.
 * 모든 셸 클래스(.sidebar/.topbar/.nav-item/.nav-parent/.nav-sub/.tb-* …)는 globals.css 에서
 * 목업 CSS verbatim 으로 정의되어 있음.
 *
 * 동적 원칙(절대):
 * - 모듈/유형 하드코딩 분기 없음. 사이드바는 서버 응답(useWorkitemTypes)으로 런타임 구성.
 * - 모듈 = nav-parent(펼침/caret + 항목수 count 배지), 하위 = 업무유형(nav-sub, sub-dot).
 * - 모듈→그룹 매핑(MODULE_GROUP, 계약서 §3.3)만 고정 테이블이고 유형 자체는 동적.
 * - 표시 라벨은 i18n(t) + 동적 메타 라벨 resolveLabel 로 해석.
 *
 * 헤더: 통합검색(⌘K) · 슈퍼관리자 설정(톱니) · 알림센터(벨) · 사용자메뉴.
 */

/** 사이드바 그룹 (계약서 §3.3 고정) */
const NAV_GROUP = {
  QUALITY: "quality",
  RISK: "risk",
  CAPABILITY: "capability",
} as const;
type NavGroup = (typeof NAV_GROUP)[keyof typeof NAV_GROUP];

/**
 * 모듈 코드 → 사이드바 그룹 매핑 (계약서 §3.3).
 * 키는 시드(modules.code) 대문자 코드. 미정의 모듈은 품질 프로세스로 폴백.
 */
const MODULE_GROUP: Record<string, NavGroup> = {
  // 품질 프로세스: 모든 모듈 — 목업(6.html) §변경관리 사이드바: 그룹 구분 없이
  // 문서관리·CAPA·위험관리·변경관리·고객불만·부적합·심사·교육·제품관리·소프트웨어밸리데이션·프로젝트관리
  // 전부 단일 "품질 프로세스" 섹션 아래 위치. "리스크"/"역량·개발" 별도 그룹 레이블 없음.
  DOC: NAV_GROUP.QUALITY,
  CAPA: NAV_GROUP.QUALITY,
  NC: NAV_GROUP.QUALITY,
  CHG: NAV_GROUP.QUALITY,
  CMP: NAV_GROUP.QUALITY,
  AUD: NAV_GROUP.QUALITY,
  INS: NAV_GROUP.QUALITY,
  CAL: NAV_GROUP.QUALITY,
  CSV: NAV_GROUP.QUALITY,
  RSK: NAV_GROUP.QUALITY,
  DSN: NAV_GROUP.QUALITY,
  TRN: NAV_GROUP.QUALITY,
  SUP: NAV_GROUP.QUALITY,
  // DB에 소문자로 저장된 모듈 코드 — .toUpperCase() 변환 후 매핑(방어적 추가)
  PRODUCT: NAV_GROUP.QUALITY,
};

/**
 * 카운트 배지를 표시하는 모듈(목업 6.html 사이드바 실측).
 * 목업 배지: CAPA·위험관리(RSK)·변경관리(CHG)·고객불만(CMP)·부적합(NC)·심사(AUD).
 * 문서관리(DOC)·교육(TRN)·제품관리·프로젝트(ALM)는 배지 없음(서브메뉴 ▸ 또는 무배지).
 */
const COUNT_BADGE_MODULES = new Set<string>(["CAPA", "CHG", "RSK", "CMP", "NC", "AUD"]);

/**
 * 카운트 배지를 빨간 crit 색으로 표시하는 모듈(목업 6.html 사이드바 실측 — nav 데이터 crit:true).
 * 고객불만(CMP)·부적합(NC)은 목업에서 crit(빨강) 배지. (.count.crit CSS 정의됨.)
 */
const CRIT_BADGE_MODULES = new Set<string>(["CMP", "NC"]);

/**
// 사이드바 nav 노출 판정(미노출 모듈 DSN·업무유형·프로비저닝·유형 권한)은 @/lib/nav/sidebarModules(SSOT)에서 import.
// 워크플로우 편집기 드롭다운도 같은 기준을 따른다(사이드바-드롭다운 정렬).

// 모듈 표시 순서 SSOT = modules.sort(DB, BE ORDER BY sort). FE 하드코딩 순서 배열 제거 —
// 사이드바·워크플로우 편집기가 같은 sort 를 따름.

/** 그룹 표시 순서 + i18n 키 */
const NAV_GROUP_ORDER: { group: NavGroup; i18nKey: string }[] = [
  { group: NAV_GROUP.QUALITY, i18nKey: "nav.group.quality" },
  { group: NAV_GROUP.RISK, i18nKey: "nav.group.risk" },
  { group: NAV_GROUP.CAPABILITY, i18nKey: "nav.group.capability" },
];

/** 모듈 그룹별 묶음(nav-parent 기준 모듈 단위) */
interface ModuleBucket {
  /** 모듈 코드 (대문자 정규화) */
  moduleCode: string;
  /** 해당 모듈의 업무유형(nav-sub) */
  types: WorkitemTypeResponse[];
  /** 모듈 항목 수(시안 사이드바 배지). 노출 유형별 서버 건수(useTypeCounts)의 합. 건수 미수신 시 배지 미표시. */
  itemCount?: number;
}

/** 업무유형 건수. 건수 응답 전이면 undefined(배지 미표시), 응답에 없는 유형은 0건. */
function typeCountOf(
  counts: Record<string, number> | undefined,
  typeId: string,
): number | undefined {
  if (!counts) return undefined;
  return (readKey(counts, typeId) as number | undefined) ?? 0;
}

/** 모듈 배지 합산 — 어느 한쪽이라도 미수신(undefined)이면 미표시 유지. */
function addCount(a: number | undefined, b: number | undefined): number | undefined {
  return a == null || b == null ? undefined : a + b;
}

/** 사이드바 모듈 아이콘 (모듈 코드별 인라인 SVG path d — 목업 톤의 18px 라인 아이콘) */
const MODULE_ICON: Record<string, string> = {
  // 아이콘 SVG path = 6.html 목업 Icons 정의(`ab91b966`) 1:1. 임의 아이콘 사용 금지.
  DOC: "M14 3v4a1 1 0 0 0 1 1h4 M5 3h9l5 5v12a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z M8 13h8 M8 17h6",
  CAPA: "M9 11l2.5 2.5L16 9 M5 4h14a1 1 0 0 1 1 1v15l-4-2-4 2-4-2-4 2V5a1 1 0 0 1 1-1z",
  CHG: "M3 7h11 M3 7l3.5-3.5 M3 7l3.5 3.5 M21 17H10 M21 17l-3.5-3.5 M21 17l-3.5 3.5",
  RSK: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z M9.5 12l1.8 1.8 3.5-3.6",
  NC: "M12 9v4 M12 17h.01 M10.3 4.3 2.5 18a2 2 0 0 0 1.7 3h15.6a2 2 0 0 0 1.7-3L13.7 4.3a2 2 0 0 0-3.4 0z",
  CMP: "M5 4h14a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H9l-4 4V5a1 1 0 0 1 1-1z M12 7v4 M12 13h.01",
  AUD: "M11 6a5 5 0 1 0 0 10 5 5 0 0 0 0-10z M15 14l5 5 M9 11h4 M11 9v4",
  TRN: "M22 9 12 5 2 9l10 4 10-4z M6 11v5c0 1 2.7 3 6 3s6-2 6-3v-5 M22 9v5",
  // ALM(프로젝트 관리) = 목업 design 아이콘. DSN 은 목업 사이드바에 없음(데모 비활성).
  ALM: "M12 2v3 M12 2a6 6 0 0 0-3 11.2V18h6v-4.8A6 6 0 0 0 12 2z M9 18v1a3 3 0 0 0 6 0v-1 M5 8H2 M22 8h-3",
  // 공급자 관리 = 공용 Icon `supplier`(가게) 아이콘과 같은 path — 추적성·프로비저닝 화면과 같은 모양.
  SUP: "M3 9l1-5h16l1 5 M4 9v11h16V9 M4 9h16 M9 13h6",
  DSN: "M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z",
  CSV: "M6 6h12v12H6z M9 9h6v6H9z M9 2v2 M15 2v2 M9 20v2 M15 20v2 M2 9h2 M2 15h2 M20 9h2 M20 15h2",
  INS: "M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2 M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2 M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2 M12 12h.01 M12 16h.01",
  CAL: "M8 6h13 M8 12h13 M8 18h13 M3 6h.01 M3 12h.01 M3 18h.01",
  // DB 소문자 코드 대문자 변환 후 매핑 대상
  PRODUCT: "M21 8 12 3 3 8v8l9 5 9-5V8z M3 8l9 5 9-5 M12 13v9",
  // 공지사항 = Green eQMS NAV notice 아이콘(flag)
  NOTICE: "M5 21V4 M5 4h11l-2 4 2 4H5",
};

/** 개요(대시보드·추적성) 아이콘 — 목업 Icons(dashboard, link) 1:1 */
const OVERVIEW_ICON = {
  dashboard: "M3 3h7v8H3z M14 3h7v5h-7z M14 11h7v10h-7z M3 14h7v7H3z",
  traceability: "M9 15l6-6 M10 6l1-1a4 4 0 0 1 6 6l-1 1 M14 18l-1 1a4 4 0 0 1-6-6l1-1",
};

/** 인라인 SVG 아이콘 (path d 문자열 다중 — 공백 분리). 외부 아이콘 패키지 비의존. */
function NavIcon({ d }: { d: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {d.split(" M").map((seg, i) => (
        <path key={i} d={i === 0 ? seg : `M${seg}`} />
      ))}
    </svg>
  );
}

/** caret(chevron) 아이콘 — nav-parent 펼침 표시 */
function CaretIcon({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform .15s" }}
    >
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

export function AppShell() {
  // 팝업(센터 모달·우측 드로어) 공통 이동/리사이즈 (데스크톱 전용). 개별 팝업 파일 미수정.
  usePopupInteractions();

  // 스펙 F §6: 라우트 이동 시 콘텐츠 영역 스크롤 맨 위로 리셋
  const contentRef = useRef<HTMLDivElement>(null);

  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { lang } = useLang();
  const { workspace: slug } = useParams<{ workspace: string }>();

  const user = useUserStore((s) => s.user);
  const clearUser = useUserStore((s) => s.clearUser);
  const queryClient = useQueryClient();
  const setWorkspace = useWorkspaceStore((s) => s.setWorkspace);
  // 마지막 사용 회사·워크스페이스 기록(로그인 직행 리졸버 용). slug 변경 시 1회만.
  // mutate 만 구조분해 — TanStack v5 에서 mutate 는 안정 참조라 effect 재실행을 유발하지 않음.
  const { mutate: touchWorkspace } = useTouchWorkspace();
  const lastTouchedSlug = useRef<string | null>(null);

  // RBAC 게이팅(UX 보조 — 신뢰 경계는 서버). 어드민 메뉴 노출은 서버 권위 역할 코드(roleCode)로 판정함.
  // me.siteAdmin(=superAdmin||companyAdmin 권한)은 dev 인증필터가 전원에게 COMPANY_ADMIN 권한을 부여해
  // 부풀려지므로(일반 사용자 Member 에게도 true) 메뉴 게이트로 부적합. roleCode 는 dev·운영 모두 정확하고
  // 설정 라우트 가드(SystemSettingsPage isAdminRoleCode)와 일관됨.
  const isSuperAdmin = user?.isSuperAdmin ?? false;
  // "설정" 메뉴는 일반관리자(회사 관리자) 전용. 어드민계정(슈퍼관리자)은 별도 "시스템 관리자 콘솔"을 쓰고
  // 일반 사용자(Member)는 관리 기능이 없으므로 둘 다 제외 — 회사 관리자(COMPANY_ADMIN) 코드만 노출.
  const isCompanyAdminOnly = user?.roleCode === SystemRole.COMPANY_ADMIN;

  // 워크스페이스 상세(활성모듈·정책) + 업무유형 목록(사이드바 동적 구성)
  const { data: wsDetail } = useWorkspaceDetail(slug);
  const { data: workitemTypes } = useWorkitemTypes(slug);
  // 업무유형별 건수(사이드바 배지) — 유형 목록과 분리된 건수 전용 조회. 아이템 변경 시 items.all 무효화로 갱신.
  const { data: typeCounts } = useTypeCounts(slug);
  // 모듈 메타(코드→다국어명) — nav-parent(모듈) 라벨을 서버 nameI18n 으로 동적 해석
  const { data: modules } = useModules();
  // 사이드바 네비게이션 구조 (섹션·항목 순서) — 데이터 기반 사이드바 렌더링
  const { data: nav } = useNav(slug);

  // 워크스페이스별 세션 유휴 로그아웃(workspaces.settings.session) — 유휴 N분 후 자동 로그아웃.
  useIdleLogout(slug);
  // 현재 사용자(/auth/me) — 푸터 조직명(companyName) 출처. TanStack Query 캐시 공유(["me"]).
  const { data: me } = useMe();

  // 메뉴 권한 게이트(UX 보조 — 서버 아이템 CRUD 게이트와 동일 규칙). 조회 불가 모듈은 사이드바에서 숨김.
  //   관리자·권한 미설정(opt-in)·미매핑 모듈은 전부 허용 → 기존 사용자 경험 불변, 제한 설정 시에만 반영.
  const menuGate = useMenuGate();

  // 모듈 코드(대문자) → 모듈 메타 룩업 (라벨 해석용). 코드는 대문자 정규화로 매칭.
  const moduleByCode = useMemo(() => {
    const map = new Map<
      string,
      { name: string; nameI18n?: Record<string, string | undefined>; sort: number }
    >();
    for (const m of modules ?? []) {
      map.set((m.code ?? "").toUpperCase(), {
        name: m.name,
        nameI18n: m.nameI18n,
        sort: m.sort ?? 999,
      });
    }
    return map;
  }, [modules]);

  // 스펙 F §6: 라우트 이동 시 .content 스크롤 맨 위 리셋 (05159942:238)
  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [location.pathname]);

  // 워크스페이스 컨텍스트 전역 적재
  useEffect(() => {
    if (wsDetail) {
      setWorkspace({
        id: wsDetail.id,
        slug: wsDetail.slug,
        name: wsDetail.name,
        enabledModules: wsDetail.enabledModules ?? [],
        modulesProvisioned: wsDetail.modulesProvisioned ?? false,
        gxpPolicy: {
          esignRequired: wsDetail.gxpPolicy?.esignRequired ?? false,
          auditTrailRetentionYears: wsDetail.gxpPolicy?.auditTrailRetentionYears ?? 0,
        },
      });
      // 진입 기록(마지막 사용 회사·WS). slug 변경 시 1회 — 네비게이션마다 중복 갱신 방지.
      if (lastTouchedSlug.current !== wsDetail.slug) {
        lastTouchedSlug.current = wsDetail.slug;
        touchWorkspace(wsDetail.slug);
      }
    }
  }, [wsDetail, setWorkspace, touchWorkspace]);

  const enabledModules = wsDetail?.enabledModules;
  const modulesProvisioned = wsDetail?.modulesProvisioned ?? false;
  const navCtx = useMemo(
    () => ({ modulesProvisioned, enabledModules, menuGate }),
    [modulesProvisioned, enabledModules, menuGate],
  );

  // 업무유형을 그룹 → 모듈(nav-parent) → 유형(nav-sub) 2단으로 분류 (활성 모듈만)
  // 레거시 폴백 렌더링(nav 미로드 시)에서 사용.
  const groupedBuckets = useMemo(() => {
    const groups: Record<NavGroup, Map<string, ModuleBucket>> = {
      [NAV_GROUP.QUALITY]: new Map(),
      [NAV_GROUP.RISK]: new Map(),
      [NAV_GROUP.CAPABILITY]: new Map(),
    };
    const types = workitemTypes ?? [];
    for (const wt of types) {
      // 비활성 모듈·사이드바 미노출 모듈/유형·유형 메뉴 권한 없음은 제외(판정 SSOT = sidebarModules).
      if (!isNavTypeShown(wt, navCtx)) {
        continue;
      }
      const code = (wt.moduleCode ?? "").toUpperCase();
      const group = readKey(MODULE_GROUP, code) ?? NAV_GROUP.QUALITY;
      const bucket = readKey(groups, group).get(code);
      const count = typeCountOf(typeCounts, wt.id);
      if (bucket) {
        bucket.types.push(wt);
        bucket.itemCount = addCount(bucket.itemCount, count);
      } else {
        readKey(groups, group).set(code, {
          moduleCode: code,
          types: [wt],
          itemCount: count,
        });
      }
    }
    // 각 모듈 안 유형 정렬 + 모듈 라벨 = 첫 유형의 모듈명(없으면 코드)
    const result: Record<NavGroup, ModuleBucket[]> = {
      [NAV_GROUP.QUALITY]: [],
      [NAV_GROUP.RISK]: [],
      [NAV_GROUP.CAPABILITY]: [],
    };
    for (const g of Object.keys(groups) as NavGroup[]) {
      const buckets = Array.from(readKey(groups, g).values());
      for (const b of buckets) {
        b.types.sort(compareNavTypes);
      }
      // 순서 = modules.sort(DB SSOT). 하드코딩 배열 제거 — 사이드바·워크플로우 편집기가 같은 sort 를 따름.
      buckets.sort(
        (a, b) =>
          (moduleByCode.get(a.moduleCode)?.sort ?? 999) -
            (moduleByCode.get(b.moduleCode)?.sort ?? 999) ||
          a.moduleCode.localeCompare(b.moduleCode),
      );
      setKey(result, g, buckets);
    }
    return result;
  }, [workitemTypes, typeCounts, navCtx, moduleByCode]);

  /**
   * 모듈 코드(대문자) → ModuleBucket 플랫 맵 — 데이터 기반 사이드바 렌더링용.
   * groupedBuckets 와 동일한 필터링 로직(isNavTypeShown)을 재사용.
   */
  const bucketByModule = useMemo(() => {
    const map = new Map<string, ModuleBucket>();
    const types = workitemTypes ?? [];
    for (const wt of types) {
      if (!isNavTypeShown(wt, navCtx)) {
        continue;
      }
      const code = (wt.moduleCode ?? "").toUpperCase();
      const existing = map.get(code);
      const count = typeCountOf(typeCounts, wt.id);
      if (existing) {
        existing.types.push(wt);
        existing.itemCount = addCount(existing.itemCount, count);
      } else {
        map.set(code, {
          moduleCode: code,
          types: [wt],
          itemCount: count,
        });
      }
    }
    // 각 버킷 내 유형 정렬
    for (const bucket of map.values()) {
      bucket.types.sort(compareNavTypes);
    }
    return map;
  }, [workitemTypes, typeCounts, navCtx]);

  // nav-parent 펼침 상태 (로컬). 현재 경로의 모듈은 자동 펼침.
  const activeModuleCode = useMemo(() => {
    // /w/:slug (신규) 와 레거시 /m/:typeCode 양쪽 모두 처리.
    const m = location.pathname.match(/\/(?:m|w)\/([^/]+)/);
    if (!m) return undefined;
    const typeCode = m[1]?.toUpperCase(); // slug(소문자) → 대문자 typeCode 정규화
    const found = (workitemTypes ?? []).find((wt) => wt.typeCode === typeCode);
    return found ? (found.moduleCode ?? "").toUpperCase() : undefined;
  }, [location.pathname, workitemTypes]);

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  // 활성 모듈은 자동 펼침(사용자가 명시적으로 접지 않은 한)
  const isExpanded = (code: string): boolean =>
    readKey(expanded, code) ?? code === activeModuleCode;
  const toggleExpanded = (code: string) =>
    setExpanded((prev) => {
      const next = { ...prev };
      setKey(next, code, !(readKey(prev, code) ?? code === activeModuleCode));
      return next;
    });

  // 푸터 조직명 — 목업(6.html): 회사/조직명("브이웨이") 표시(워크스페이스명 아님).
  // 서버 권위 companyName(companies.name) 우선, 없으면 워크스페이스명/slug 폴백. 인증 운영 설명은 정적 i18n.
  const orgName = me?.companyName ?? wsDetail?.name ?? slug ?? "";

  const goTo = (path: string) => navigate(slug ? `/${slug}/${path}` : `/${path}`);

  /**
   * 시스템 어드민(공급사) 콘솔로 이동.
   * - prod(빌드 주소 VITE_APP_URL 이 도메인): admin.<빌드 주소 호스트> 루트로 전체 페이지 이동.
   *   (예: eqms.co.kr → admin.eqms.co.kr) — 별도 도메인 원칙(00-menu-structure §47).
   *   현재 호스트의 첫 라벨을 바꾸는 방식은 eqms.co.kr 을 admin.co.kr(남의 도메인)로 만들므로 쓰지 않음.
   * - dev/localhost·IP(서브도메인 불가) 또는 이미 admin.*: SPA 내부 /admin/provisioning 경로로 이동.
   */
  const goToAdminConsole = () => {
    if (typeof window === "undefined") return;
    const { hostname, protocol } = window.location;
    const appUrl = import.meta.env.VITE_APP_URL;
    const appHost = appUrl ? new URL(appUrl).hostname : "";
    const isDomain = appHost.includes(".") && !/^[\d.]+$/.test(appHost);
    if (isDomain && !hostname.startsWith("admin.")) {
      window.location.href = `${protocol}//admin.${appHost}/provisioning`;
      return;
    }
    navigate("/admin/provisioning");
  };

  // 대시보드 접이식 그룹(목업 20260720): 개요 › 대시보드 > 개인/전체 2서브메뉴.
  const onDashboardPersonal = location.pathname.includes("/dashboard/personal");
  const onDashboardOverall = location.pathname.includes("/dashboard/overall");
  const onDashboard = location.pathname.includes("/dashboard");
  const onTraceability = location.pathname.endsWith("/traceability");
  const onProjects = location.pathname.includes("/projects");

  /** 모듈 클릭: 펼침 토글 + 첫 유형 목록으로 이동 */
  const onModuleClick = (bucket: ModuleBucket) => {
    toggleExpanded(bucket.moduleCode);
    const first = bucket.types[0];
    if (first) goTo(`w/${first.typeCode.toLowerCase()}`);
  };

  // 사용자 표시명/직책 — 헤더 직책 라벨은 직책(positionName: 예 "품질책임자(QA)") 우선,
  // 미제공 시 역할 표시명(roleLabel — 코드 번역) 폴백. (RBAC 게이트는 roleCode 별도 — 표시 분리)
  const userName = user?.name ?? t("user.guest", "게스트");
  const userRole = user?.positionName ?? roleLabel(user?.roleCode, user?.roleName);

  return (
    // 하이브리드: Mantine AppShell 골격 유지. 헤더/네비 슬롯은 쓰지 않고 본문 한 칸에
    // 목업 셸을 직접 렌더함. Main 의 기본 padding/min-height 를 제거해 목업 .eqms-shell
    // 이 뷰포트 전체(100vh)를 자체 레이아웃으로 차지하도록 한다(이중 스크롤 방지).
    <MantineAppShell
      padding={0}
      styles={{
        root: { "--app-shell-padding": "0px" } as CSSProperties,
        main: {
          backgroundColor: "var(--bg)",
          minHeight: "100vh",
          padding: 0,
          paddingTop: 0,
        },
      }}
    >
      <MantineAppShell.Main>
        <div className="eqms-shell">
          {/* ===== 사이드바 ===== */}
          <nav className="eqms-sidebar">
            {/* 브랜드 — 회사 로고(settings.logoKey) 있으면 로고, 없으면 기본 "Teamer eQMS". */}
            <div className="sb-brand">
              <CompanyBrand />
            </div>

            {/* 조직 전환 스위처 — 회사·워크스페이스 전환(사용자 계정 메뉴에서 분리한 전용 드롭다운). */}
            <OrgSwitcher />

            {/* 스크롤 네비 */}
            <div className="sb-scroll">
              {(() => {
                // ── 재사용 렌더 헬퍼 (마크업 동일, 데이터 기반·레거시 양쪽에서 호출) ──

                /** 대시보드 접이식 블록 (개인/전체 서브메뉴) — 목업 20260720 §개요 */
                const renderDashboardItem = () => {
                  // 하위 메뉴별 권한(canDashboard) — 둘 다 숨으면 블록 전체 숨김, 상위 클릭은 보이는 첫 하위로 이동.
                  const firstSubPath = firstDashboardPath(menuGate);
                  if (firstSubPath === null) return null;
                  const showPersonal = menuGate.canDashboard(MENU_DASHBOARD_PERSONAL);
                  const showOverall = menuGate.canDashboard(MENU_DASHBOARD_OVERALL);
                  const open = expanded["__DASH__"] ?? onDashboard;
                  return (
                    <div key="__DASH__">
                      <button
                        className={`nav-item nav-parent${onDashboard ? " kidactive" : ""}`}
                        onClick={() => {
                          toggleExpanded("__DASH__");
                          goTo(firstSubPath);
                        }}
                      >
                        <NavIcon d={OVERVIEW_ICON.dashboard} />
                        <span>{t("nav.dashboard", "대시보드")}</span>
                        <span
                          className="nav-caret"
                          role="button"
                          tabIndex={-1}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpanded("__DASH__");
                          }}
                        >
                          <CaretIcon open={open} />
                        </span>
                      </button>
                      {open && (
                        <>
                          {showPersonal && (
                            <button
                              className={`nav-item nav-sub${onDashboardPersonal ? " active" : ""}`}
                              onClick={() => goTo(NAV_PATH.DASHBOARD_PERSONAL)}
                            >
                              <span className="sub-dot" />
                              <span>{t("nav.dashboardPersonal", "개인 대시보드")}</span>
                            </button>
                          )}
                          {showOverall && (
                            <button
                              className={`nav-item nav-sub${onDashboardOverall ? " active" : ""}`}
                              onClick={() => goTo(NAV_PATH.DASHBOARD_OVERALL)}
                            >
                              <span className="sub-dot" />
                              <span>{t("nav.dashboardOverall", "전체 대시보드")}</span>
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  );
                };

                /** 추적성 단일 nav-item — 목업 20260720 §개요 */
                const renderTraceabilityItem = () => (
                  <button
                    key="__TRACE__"
                    className={`nav-item${onTraceability ? " active" : ""}`}
                    onClick={() => goTo("traceability")}
                  >
                    <NavIcon d={OVERVIEW_ICON.traceability} />
                    <span>{t("nav.traceability", "추적성")}</span>
                  </button>
                );

                /** 프로젝트 관리 단일 nav-item — 프로젝트 템플릿은 설정 › 프로젝트 템플릿 탭(Green eQMS). */
                const renderProjectsItem = () => (
                  <button
                    key="__ALM__"
                    className={`nav-item${onProjects ? " active" : ""}`}
                    onClick={() => goTo("projects")}
                  >
                    <NavIcon d="M12 2v3 M12 2a6 6 0 0 0-3 11.2V18h6v-4.8A6 6 0 0 0 12 2z M9 18v1a3 3 0 0 0 6 0v-1 M5 8H2 M22 8h-3" />
                    <span>{t("nav.projects", "프로젝트 관리")}</span>
                  </button>
                );

                /**
                 * 모듈 버킷 렌더 (리프 또는 nav-parent+nav-sub).
                 * 유형 1개 이하 → 리프(nav-item), 2개 이상 → 펼침(nav-parent + nav-sub 목록).
                 */
                const renderModuleBucket = (bucket: ModuleBucket) => {
                  // 메뉴 권한: 이 모듈 조회 권한이 없으면 사이드바에서 숨김(서버 게이트와 일치).
                  if (!menuGate.canRead(bucket.moduleCode)) return null;
                  // 모듈 표시 라벨: 서버 모듈 메타(nameI18n) 우선 해석(동적).
                  const meta = moduleByCode.get(bucket.moduleCode);
                  const moduleLabel = resolveModuleLabel(
                    meta,
                    bucket.types[0],
                    bucket.moduleCode,
                    lang,
                  );
                  const iconD = MODULE_ICON[bucket.moduleCode] ?? MODULE_ICON.DOC;
                  // 모듈 항목 수(시안 배지). 카운트 노출 모듈(CAPA/CHG/RSK/CMP/NC/AUD)만 표시.
                  // 교육·프로젝트 등 확장형 모듈은 합산 배지가 목업과 어긋나므로 미표시(00-shell §A-2).
                  const itemCount = COUNT_BADGE_MODULES.has(bucket.moduleCode)
                    ? bucket.itemCount
                    : undefined;
                  // 고객불만·부적합 배지는 빨간 crit 색(목업 nav 데이터 crit:true).
                  const critBadge = CRIT_BADGE_MODULES.has(bucket.moduleCode);
                  // 시안: 유형이 2개 이상인 모듈만 펼침(nav-parent). 단일 유형 모듈은
                  // 리프(nav-item) — 모듈명만 표시하고 클릭 시 그 유형 목록으로 이동.
                  if (bucket.types.length <= 1) {
                    const wt = bucket.types[0];
                    const leafActive = !!wt && isTypePathActive(location.pathname, wt.typeCode);
                    return (
                      <button
                        key={bucket.moduleCode}
                        className={`nav-item${leafActive ? " active" : ""}`}
                        onClick={() => wt && goTo(`w/${wt.typeCode.toLowerCase()}`)}
                      >
                        <NavIcon d={iconD} />
                        <span>{moduleLabel}</span>
                        {itemCount != null && (
                          <span className={`count${critBadge ? " crit" : ""}`}>{itemCount}</span>
                        )}
                      </button>
                    );
                  }
                  const open = isExpanded(bucket.moduleCode);
                  const kidActive = activeModuleCode === bucket.moduleCode;
                  return (
                    <div key={bucket.moduleCode}>
                      <button
                        className={`nav-item nav-parent${kidActive ? " kidactive" : ""}`}
                        onClick={() => onModuleClick(bucket)}
                      >
                        <NavIcon d={iconD} />
                        <span>{moduleLabel}</span>
                        {itemCount != null && (
                          <span className={`count${critBadge ? " crit" : ""}`}>{itemCount}</span>
                        )}
                        <span
                          className="nav-caret"
                          role="button"
                          tabIndex={-1}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpanded(bucket.moduleCode);
                          }}
                        >
                          <CaretIcon open={open} />
                        </span>
                      </button>
                      {/* 하위 업무유형(nav-sub) */}
                      {open &&
                        bucket.types.map((wt) => {
                          // 경로 세그먼트 단위 비교 — /w/trn 이 /w/trn_record 에서도 선택되지 않게.
                          const isActive = isTypePathActive(location.pathname, wt.typeCode);
                          return (
                            <button
                              key={wt.id}
                              className={`nav-item nav-sub${isActive ? " active" : ""}`}
                              onClick={() => goTo(`w/${wt.typeCode.toLowerCase()}`)}
                            >
                              <span className="sub-dot" />
                              <span>{resolveLabel(wt.name, wt.nameI18n, lang)}</span>
                            </button>
                          );
                        })}
                    </div>
                  );
                };

                // ── 데이터 기반 렌더 (nav 로드 완료 시) ──────────────────────────────
                if (nav) {
                  return (
                    <>
                      {nav.sections.map((section) => (
                        <div key={section.id}>
                          <div className="sb-group-label">{navSectionTitle(section, lang)}</div>
                          {section.entries.map((entry) => {
                            let el: ReactNode = null;
                            const ref = entry.ref.toUpperCase();
                            if (entry.kind === "system" && entry.ref === "dashboard") {
                              el = renderDashboardItem();
                            } else if (entry.kind === "module" && ref === "TRACE") {
                              el = isTraceMenuConfigured(navCtx) ? renderTraceabilityItem() : null;
                            } else if (entry.kind === "module" && ref === PROJECTS_NAV_REF) {
                              el = isProjectsMenuShown(navCtx) ? renderProjectsItem() : null;
                            } else if (entry.kind === "module") {
                              const bucket = bucketByModule.get(ref);
                              el = bucket ? renderModuleBucket(bucket) : null; // 비활성 모듈 → 스킵
                            }
                            return <Fragment key={entry.id}>{el}</Fragment>;
                          })}
                        </div>
                      ))}
                    </>
                  );
                }

                // ── 레거시 폴백 (nav 로딩·에러 시 — 기존 하드코딩 구조 유지) ─────────
                return (
                  <>
                    {/* 개요: 대시보드 · 추적성 */}
                    <div>
                      <div className="sb-group-label">{t("nav.group.overview", "개요")}</div>
                      {renderDashboardItem()}
                      {isTraceMenuConfigured(navCtx) && renderTraceabilityItem()}
                    </div>

                    {/* 품질 프로세스 / 리스크 / 역량·개발 — 모듈(nav-parent) + 유형(nav-sub) */}
                    {NAV_GROUP_ORDER.map(({ group, i18nKey }) => {
                      const buckets = readKey(groupedBuckets, group);
                      const showGroup = buckets.length > 0 || group === NAV_GROUP.CAPABILITY;
                      if (!showGroup) return null;
                      return (
                        <div key={group}>
                          {/* 목업: QUALITY("품질 프로세스") 레이블만 표시. RISK·CAPABILITY는 레이블 없음. */}
                          {group === NAV_GROUP.QUALITY && (
                            <div className="sb-group-label">{t(i18nKey)}</div>
                          )}
                          {buckets.map((bucket) => renderModuleBucket(bucket))}
                          {/* 역량·개발: 프로젝트 관리(ALM) — DSN(프로젝트관리) 프로비저닝 연동 */}
                          {group === NAV_GROUP.CAPABILITY &&
                            isProjectsMenuShown(navCtx) &&
                            renderProjectsItem()}
                        </div>
                      );
                    })}
                  </>
                );
              })()}

              {/* 시스템 설정 (회사 관리자) — 목업: 단일 항목(gear). 클릭 시 설정 2탭 화면(D-10:
                  사이트 관리자·인원 관리). 하위 분리항목(워크스페이스/필드빌더/코드/사용자/역할)은
                  설정 화면 내부 탭으로 통합 — 사이드바엔 단일 "시스템 설정"만 노출(30-system-settings A).
                  어드민계정(슈퍼관리자)은 제외 — 일반관리자(회사 관리자) 권한만 노출. */}
              {isCompanyAdminOnly && (
                <>
                  <div>
                    <button
                      className={`nav-item${
                        location.pathname === "/settings" ||
                        (location.pathname.startsWith("/settings/") &&
                          !location.pathname.startsWith("/settings/account"))
                          ? " active"
                          : ""
                      }`}
                      onClick={() => goTo("settings")}
                    >
                      <NavIcon d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                      <span>{t("nav.systemSettings", "설정")}</span>
                    </button>
                  </div>
                  {/* 목업(6.html): 사이드바에는 "시스템 설정" 단일 항목만 표시.
                      워크스페이스 설정·필드 빌더·코드 관리·사용자 관리·역할·권한은
                      설정 화면 내부 탭/섹션으로 진입(사이드바 미노출). */}
                </>
              )}
              {/* 시스템 어드민(공급사) 콘솔 진입 — 회사 사이드바가 아닌 별도 셸(AdminAppShell)로 분리.
                  회사 셸 사이드바에는 모듈 프로비저닝 항목을 두지 않는다(헤더 톱니로만 진입). */}
              {isSuperAdmin && (
                <div>
                  <button className="nav-item" onClick={goToAdminConsole}>
                    <NavIcon d="M20 7h-9 M14 17H5 M17 17a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M7 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
                    <span>{t("nav.adminConsoleMenu", "시스템 관리자 콘솔")}</span>
                  </button>
                </div>
              )}
            </div>

            {/* 푸터: 조직(회사)명 — 목업: 회사/조직명("브이웨이") */}
            <div className="sb-footer">
              <div className="sb-org">
                <div className="txt">
                  <b>{orgName}</b>
                </div>
              </div>
            </div>
          </nav>

          {/* ===== 메인(헤더 + 콘텐츠) ===== */}
          <main className="eqms-main">
            {/* 상단 헤더 */}
            <header className="topbar">
              {/* 통합검색 — 클릭/⌘K(Win: Ctrl+K)로 인풋 포커스, 인라인 드롭다운 결과 */}
              <GlobalSearch />

              <div className="tb-spacer" />

              {/* 슈퍼관리자 설정(톱니) — 시스템 어드민 콘솔(별도 셸/도메인) 진입점. 스펙 F §2-4: adminOnly */}
              {isSuperAdmin && (
                <button
                  className="tb-icon-btn"
                  title={t("nav.adminConsole", "슈퍼 관리자 설정")}
                  aria-label={t("nav.adminConsole", "슈퍼 관리자 설정")}
                  onClick={goToAdminConsole}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.8}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                  </svg>
                </button>
              )}

              {/* 알림센터(벨 + 미읽음 배지) — 동작 위젯 재사용 */}
              <NotificationCenter />

              {/* 사용자 메뉴 (워크스페이스 변경 · 언어전환 · 로그아웃) */}
              <Menu shadow="md" width={220} position="bottom-end">
                <Menu.Target>
                  <UnstyledButton className="tb-user" aria-label={t("user.menu", "사용자 메뉴")}>
                    <span
                      className="avatar"
                      style={{ background: avatarColorFor(user?.sub ?? user?.email, userName) }}
                    >
                      {initialsOf(userName)}
                    </span>
                    <div>
                      <div className="nm">{userName}</div>
                      {userRole && <div className="rl">{userRole}</div>}
                    </div>
                    {/* 사용자명 우측 드롭다운 chevron(▾) — 목업 헤더 표시 */}
                    <svg
                      className="chev"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </UnstyledButton>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>{user?.email ?? ""}</Menu.Label>
                  {/* 사용자 설정(D-14) — 언어·버전·프로필. 헤더 다국어 전환을 이 화면으로 이동. */}
                  <Menu.Item onClick={() => goTo("settings/account")}>
                    {t("account.title", "사용자 설정")}
                  </Menu.Item>
                  {/* 회사·워크스페이스 전환은 사이드바 상단 조직 스위처(OrgSwitcher)로 분리 — 계정 메뉴는 계정만. */}
                  {/* 팀원 초대(D-02)는 설정 › 인원 관리 탭(PersonnelPage)으로 이동. */}
                  <Menu.Divider />
                  <Menu.Item
                    onClick={async () => {
                      // 로그아웃: 스토어·토큰·활성회사 정리 후 로그인 화면으로 (auth-guard/로그인 코드 불변)
                      clearUser();
                      setActiveCompany(null); // 다회사 활성 컨텍스트 정리(세션 간 누수 방지)
                      // 이전 세션의 서버상태 캐시(특히 ["me"])를 폐기 — 재로그인 시 이전 사용자
                      // 잔존(useMe 캐시가 스토어를 덮어써 상단 사용자 미갱신) 방지.
                      queryClient.clear();
                      await logout();
                      navigate("/login", { replace: true });
                    }}
                  >
                    {t("user.logout", "로그아웃")}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </header>

            {/* 비밀번호 만료 하드게이트(전역 보안정책) — /me passwordExpired=true 이면 앱 위에
                닫을 수 없는 차단 모달을 띄워 변경을 강제함. 라우팅을 건드리지 않는 오버레이라
                리다이렉트 루프가 발생하지 않는다(소프트 배너에서 하드게이트로 전환). */}
            <PasswordExpiredGate />

            {/* 콘텐츠 — 목업 .content (스크롤). 여백 래퍼 .page 는 각 페이지 루트가 가짐(이중 패딩 방지) */}
            <div className="content" ref={contentRef}>
              <Outlet />
            </div>
          </main>
        </div>
      </MantineAppShell.Main>

      {/* 토스트 (스펙 F §5 — .toast-wrap/.toast) — 공용 ToastHost로 분리 */}
      <ToastHost />
    </MantineAppShell>
  );
}
